import { useCallback, useMemo } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { Form } from '@/_shared/components/Form';
import { useDialog } from '@/_shared/hooks/useDialog';
import { UserStatus } from '@/organization/state/types/users';

type Props = {
  filters?: { search?: string; status?: string[] };
  setFilters?: (filters: { search?: string; status?: string[] }) => void;
};

export const FilterDelegates = ({ setFilters, filters }: Props) => {
  const t = useTranslations('settings.tabs.delegates.filterDelegates');
  const tShared = useTranslations('userStatus');
  const { closeDialog: closeDialogFilter } = useDialog('filters');

  const statusItems = useMemo(() => {
    return Object.keys(UserStatus).map(key => ({
      label: tShared(key),
      value: UserStatus[key as keyof typeof UserStatus],
    }));
  }, [tShared]);

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
  } = useForm();

  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    data => {
      const status = Array.isArray(data.status)
        ? data.status.map((d: { value: string }) => d.value)
        : undefined;
      if (isValid && setFilters) {
        setFilters({
          search: filters?.search || '',
          status,
        });
        closeDialogFilter();
      }
    },
    [closeDialogFilter, filters?.search, isValid, setFilters],
  );

  const onReset = useCallback(() => {
    if (setFilters) {
      setFilters({
        search: filters?.search || '',
        status: statusItems.map(status => status.value),
      });
      setValue('status', statusItems);
      closeDialogFilter();
    }
  }, [setFilters, filters?.search, statusItems, setValue, closeDialogFilter]);

  return (
    <Box>
      <Box display='flex' flexDirection='column'>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form.FormDropdownMultiple
            control={control}
            name='status'
            label={t('status.label')}
            items={statusItems || []}
            searchActive
            labelStyle={{ color: 'white', mb: 'xxs' }}
            placeholder={t('create.selectResources')}
            additonalProps={{
              required: true,
            }}
            defaultValue={statusItems.map(status => ({
              value: status.value,
              label: status.label,
            }))}
            data-testid='dropdown-select-status'
            optionTestId='dropdown-select-status-option'
          />
          <Box justifyContent='flex-end' display='flex' flexDirection='row'>
            <Form.Buttons.Cta onClick={onReset} mt='l' mr='xs'>
              {t('buttons.cancel')}
            </Form.Buttons.Cta>
            <Form.Buttons.Submit disabled={!isValid} mt='l' data-testid='filter-button-search'>
              {t('buttons.save')}
            </Form.Buttons.Submit>
          </Box>
        </form>
      </Box>
    </Box>
  );
};
